/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/fields.js
 *
 * Purpose: Miscellaneous non-API hooks for entity display names, portal view settings,
 *     filtered/sorted additional-field configs, and responsive column counts.
 *
 * Scope: Entity naming customization, per-portal-view settings persisted in local/session
 *     storage, and additional-field filtering used by order/entity forms.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: `useEntityDisplayName` reads the customizable singular/plural display name for
 *     an entity type out of `sessionStorage`, falling back to empty strings if unset.
 *     `usePortalView` reads a JSON-encoded portal view config out of `localStorage`, optionally
 *     scoped to one config key. `useFilteredFieldsConfigs` chains a small builder-pattern
 *     object (`filterEntity`) to filter additional-field configs down to fields flagged as
 *     additional attributes of supported types, sort them by portal order, and normalize
 *     single-select fields to multi-select. `useColsCount` returns a column count that
 *     switches between mobile and desktop constants based on `useResponsive`.
 *
 * Exports: useEntityDisplayName, usePortalView, useFilteredFieldsConfigs, useColsCount
 */

import React from 'react';
import {
  AddlFieldType,
  NUMBER_OF_CUSTOM_COLUMNS_ORDER_ADDITIONAL_INFO,
  NUMBER_OF_CUSTOM_COLUMNS_ORDER_ADDITIONAL_INFO_MOBILE,
} from 'shared/constants/fields';
import { safeJsonParse } from 'shared/utils/functions';
import useResponsive from './useResponsive';

export const useEntityDisplayName = (entityType) => {
  let conf = sessionStorage.getItem('entityDisplayNames');
  if (conf && entityType) {
    conf = JSON.parse(conf)[entityType];
    if (conf) return [conf.singular, conf.plural];
  }
  return ['', ''];
};

export const usePortalView = (config = '') => {
  const conf = localStorage.getItem('portalView');
  const res = safeJsonParse(conf);
  if (res) return config ? res[config] : res;
  return res;
};

export const useFilteredFieldsConfigs = (configs = []) => {
  const data = Object.assign(filterEntity, { configs })
    .filterByAdditionalAttribute()
    .filterByType()
    .sortByOrder()
    .replaceSelectToMultiSelect();

  return data.configs;
};

const filterEntity = {
  filterByAdditionalAttribute: function () {
    this.configs = this.configs.filter((c) => c.additionalAttribute);
    return this;
  },
  filterByType: function () {
    this.configs = this.configs.filter((c) =>
      [
        AddlFieldType.NUM,
        AddlFieldType.STR,
        AddlFieldType.SELECT,
        AddlFieldType.SELECT_MULTI,
        AddlFieldType.DATE,
        AddlFieldType.DATETIME,
      ].includes(c.fieldType),
    );
    return this;
  },
  sortByOrder: function () {
    this.configs = this.configs.sort(
      (a, b) => Number.parseInt(a.portalOrder) - Number.parseInt(b.portalOrder),
    );
    return this;
  },
  replaceSelectToMultiSelect: function () {
    this.configs = this.configs.map((c) => {
      if (c.fieldType === AddlFieldType.SELECT) {
        return {
          ...c,
          fieldType: AddlFieldType.SELECT_MULTI,
        };
      }
      return c;
    });
    return this;
  },
};

export const useColsCount = () => {
  const { isMobile, isDesktop } = useResponsive();
  const [count, setCount] = React.useState(
    NUMBER_OF_CUSTOM_COLUMNS_ORDER_ADDITIONAL_INFO,
  );
  React.useEffect(() => {
    if (isMobile) {
      setCount(NUMBER_OF_CUSTOM_COLUMNS_ORDER_ADDITIONAL_INFO_MOBILE);
      return;
    }
    if (isDesktop) {
      setCount(NUMBER_OF_CUSTOM_COLUMNS_ORDER_ADDITIONAL_INFO);
      return;
    }
  }, [isMobile, isDesktop]);

  return [count];
};
